// Coalesce sdk.getWorkspace(wsId) calls. A single org/workspace switch was
// firing getWorkspace up to 8× — onWorkspaceChange + loadWorkspaceProject, each
// re-run by the orgSwitched-bus + auth `active_workspace_id` dual-trigger — and
// the 8 concurrent calls saturated the browser's ~6-connection pool so EACH took
// 2-4s instead of the ~170ms it takes uncontended, pinning the switch's pre-wipe
// window at ~3.8s (and starving the roster / dashboard-prefs reads racing them).
// In-flight dedup + a short TTL keyed by workspaceId collapses them to ONE
// round-trip. Same pattern as listOrgMembers.js / loadAllTeams.
let _inflight = {}
let _cache = {}
const TTL_MS = 8000

// Returns the UNWRAPPED workspace record (res.data ?? res) or null. Callers keep
// their own cross-org / settings guards.
export const getWorkspaceCached = (sdk, wsId, opts = {}) => {
  if (!sdk || wsId == null || typeof sdk.getWorkspace !== 'function') {
    return Promise.resolve(null)
  }
  const key = String(wsId)
  if (!opts.force) {
    const c = _cache[key]
    if (c && Date.now() - c.at < TTL_MS) return Promise.resolve(c.rec)
    // In-flight reuse is TIME-BOUNDED (WS-SWITCH-ONWORKSPACECHANGE-BOUNDED-
    // WINDOW-TRIP-1). The entry is dropped in `.finally` — which NEVER runs for
    // a request that neither resolves nor rejects, so one hung round-trip used
    // to poison this workspace id for the rest of the session: every later
    // caller (the switch pipeline, loadWorkspaceProject, the config resolver)
    // awaited the same dead promise. Past the TTL a fresh fetch is issued
    // instead; the dead one is simply abandoned.
    const f = _inflight[key]
    if (f && Date.now() - f.at < TTL_MS) return f.p
  }
  const p = Promise.resolve(sdk.getWorkspace(wsId))
    .then((res) => {
      const raw = (res && res.data) || res || null
      // Only an id-shaped payload is a workspace RECORD. SDK error payloads
      // ({ error: 'no active workspace for user', … }) resolve (not reject) —
      // caching one as "the record" poisoned every consumer for TTL_MS
      // (settings unresolvable across the whole boot chain: module loader,
      // config resolver, workspace-apps). Return null instead — every caller
      // already treats null as fetch-miss.
      const rec = raw && (raw._id || raw.id) ? raw : null
      if (rec) _cache[key] = { rec, at: Date.now() }
      return rec
    })
    .finally(() => {
      if (_inflight[key] && _inflight[key].p === p) delete _inflight[key]
    })
  _inflight[key] = { p, at: Date.now() }
  return p
}

// Drop a workspace's cached record (call after a settings mutation) so the next
// read re-fetches — mirrors invalidateOrgMembers.
export const invalidateWorkspaceCache = (wsId) => {
  if (wsId == null) {
    _cache = {}
    _inflight = {}
  } else {
    const k = String(wsId)
    delete _cache[k]
    delete _inflight[k]
  }
}

// Seed the cache with a record that arrived on another read - the composite
// boot's workspace list rows and its `workspace` section use the SAME server
// projection as a single getWorkspace read (WORKSPACE_READ_PROJECTION +
// platform defaults + resolvedAppearance). bootShell primes them so the eager
// scope read AND the first auth tick's list-row upgrade (shared
// setActiveWorkspaceRecord) both answer from the cache: zero record requests
// on a cold load. Only an id-shaped record is stored; an entry already in
// flight is left alone.
export const primeWorkspaceCache = (wsId, rec) => {
  if (wsId == null || !rec || !(rec._id || rec.id)) return false
  if (String(rec._id || rec.id) !== String(wsId)) return false
  _cache[String(wsId)] = { rec, at: Date.now() }
  return true
}
